import React from "react";
import "../styles/Features.css";
import { features } from "../utils/constants";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../context/Language";

/*
  Shared image block.
  Class names must match Features.css:
  - .feature-images / .feature-images-responsive (container)
  - .feature-images-multiple (two-image composition)
  - .feature-device-image (every phone image)
  - .feature-device-image-secondary (second, tilted phone)
*/
const DeviceImages = ({ images, language, alt, responsive }) => {
  const hasTwoImages = images.length > 1;
  const containerClass = [
    responsive ? "feature-images-responsive" : "feature-images",
    hasTwoImages ? "feature-images-multiple" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClass}>
      {images.map((image, index) => (
        <img
          key={index}
          src={image[language] || image["en"]}
          alt={alt}
          className={
            index === 1
              ? "feature-device-image feature-device-image-secondary"
              : "feature-device-image"
          }
        />
      ))}
    </div>
  );
};

const FeatureText = ({ title, description, comingSoon, responsive, t }) => (
  <div className={responsive ? "feature-text-responsive" : "feature-text"}>
    {comingSoon && (
      <span className="feature-coming-soon">{t("Coming soon")}</span>
    )}
    <h3>{title}</h3>
    <p>{description}</p>
  </div>
);

const FeatureItem = ({
  title,
  description,
  images,
  alignment,
  language,
  comingSoon,
  t,
}) => {
  const titlePath = title[language] || title["en"];
  const descriptionPath = description[language] || description["en"];

  const imagesBlock = (
    <DeviceImages images={images} language={language} alt={titlePath} />
  );

  return (
    <div className="feature-item">
      {alignment === "left" && imagesBlock}
      <FeatureText
        title={titlePath}
        description={descriptionPath}
        comingSoon={comingSoon}
        t={t}
      />
      {alignment === "right" && imagesBlock}
    </div>
  );
};

const FeatureItemResponsive = ({
  title,
  description,
  images,
  language,
  comingSoon,
  t,
}) => {
  const titlePath = title[language] || title["en"];
  const descriptionPath = description[language] || description["en"];

  return (
    <div className="feature-item-responsive">
      <FeatureText
        title={titlePath}
        description={descriptionPath}
        comingSoon={comingSoon}
        responsive
        t={t}
      />
      <DeviceImages
        images={images}
        language={language}
        alt={titlePath}
        responsive
      />
    </div>
  );
};

const Features = () => {
  const { language } = useLanguage();
  const { t } = useTranslation();

  return (
    <>
      <section className="features-section">
        {features.map((feature, index) => (
          <FeatureItem
            key={index}
            title={feature.title}
            description={feature.description}
            images={feature.images}
            alignment={index % 2 === 0 ? "left" : "right"}
            language={language.value}
            comingSoon={feature.comingSoon}
            t={t}
          />
        ))}
        <p className="andMuchMore">{t("And much more!")}</p>
      </section>

      <section className="features-section-responsive">
        {features.map((feature, index) => (
          <FeatureItemResponsive
            key={index}
            title={feature.title}
            description={feature.description}
            images={feature.images}
            language={language.value}
            comingSoon={feature.comingSoon}
            t={t}
          />
        ))}
        <p className="andMuchMore">{t("And much more!")}</p>
      </section>
    </>
  );
};

export default Features;
